import { useRef } from "react";

import { Controller } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import { useHover } from "usehooks-ts";

import { Select } from "@components/v2";
import { Body } from "@components/v2/typography/body";
import { Label } from "@components/v2/typography/label";
import { useTimeSelectOptions } from "@features/data-export/components/schedule-configurator/hooks";
import { weekDays } from "@north-beam/nb-common";

import { useScheduleExportFormContext } from "../hooks/useScheduleExportFormContext";

import {
  frequencyOptions,
  monthlyScheduleDayOptions,
} from "./time-frame-schema";

export const ScheduleTimeFrameSubForm = ({
  index,
  onDelete,
}: {
  index: number;
  onDelete?(): void;
}) => {
  const timeFrameContainerRef = useRef(null);
  const isHovering = useHover(timeFrameContainerRef);

  const scheduleExportForm = useScheduleExportFormContext();

  const timeOptions = useTimeSelectOptions();

  const frequency = scheduleExportForm.watch(
    `trigger.timeFrames.${index}.frequency`,
  );

  return (
    <div
      className="flex gap-2 items-end max-w-full w-fit"
      ref={timeFrameContainerRef}
    >
      <Body
        variant={200}
        color={isHovering ? "error" : undefined}
        fontWeight="semibold"
        className={twMerge(
          "flex justify-center items-center mr-2 w-9 h-9 rounded-md border border-v2-tertiary aspect-square",
          "cursor-pointer  hover:border-v2-action-negative-hover",
        )}
        onClick={() => isHovering && onDelete?.()}
      >
        {isHovering ? <i className="fa-regular fa-trash" /> : index + 1}
      </Body>
      <Label label="Frequency" variant={300} className="max-w-sm w-fit">
        <Controller
          name={`trigger.timeFrames.${index}.frequency`}
          control={scheduleExportForm.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              size="md"
              value={field.value}
              options={[...frequencyOptions]}
              onChange={field.onChange}
              buttonClassName="min-w-[170px]"
            />
          )}
        />
      </Label>
      {frequency === "WEEKLY" && (
        <Label label="Day" variant={300} className="max-w-sm w-fit">
          <Controller
            name={`trigger.timeFrames.${index}.weekDay`}
            control={scheduleExportForm.control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                size="md"
                value={field.value}
                options={weekDays.map((day) => ({ value: day, label: day }))}
                onChange={field.onChange}
                buttonClassName="min-w-[170px]"
              />
            )}
          />
        </Label>
      )}
      {frequency === "MONTHLY" && (
        <Label label="Day" variant={300} className="max-w-sm w-fit">
          <Controller
            name={`trigger.timeFrames.${index}.day`}
            control={scheduleExportForm.control}
            rules={{ required: true }}
            render={({ field }) => (
              <Select
                size="md"
                value={field.value}
                options={[...monthlyScheduleDayOptions]}
                onChange={field.onChange}
                buttonClassName="min-w-[180px]"
              />
            )}
          />
        </Label>
      )}
      <Label label="Time" variant={300} className="max-w-sm w-fit">
        <Controller
          name={`trigger.timeFrames.${index}.time`}
          control={scheduleExportForm.control}
          rules={{ required: true }}
          render={({ field }) => (
            <Select
              size="md"
              value={field.value}
              options={timeOptions}
              onChange={field.onChange}
              buttonClassName="min-w-[170px]"
            />
          )}
        />
      </Label>
    </div>
  );
};
